import { useMemo } from "react";

import clsx from "clsx";
import { AnimatePresence, motion } from "framer-motion";
import moment from "moment";

import cardLoading from "@assets/sales-v3-platform-loading-cell.gif";
import { Tooltip } from "@components/tooltip-container";
import { useUser } from "@components/user-context";
import { formatting } from "@north-beam/nb-common";
import { type MetricPrecision } from "@north-beam/nb-common/formatting";
import { useClientSettings } from "@pages/mmm/hooks/use-client-settings";

import { PacingStatus } from "../../shared/pacing-status";
import { usePerformanceTrackingKpiData } from "../hooks/use-performance-tracking-kpi-data";

import { Slider } from "./slider";

type Props = {
  data?: {
    name: string;
    actual: number;
    budgeted: number;
    target: number;
    predictedRange: {
      low: number;
      median: number;
      high: number;
    };
  };
};

export function useBudgetedKPIToDate() {
  const { trackingKpiData, loading } = usePerformanceTrackingKpiData();
  const { user } = useUser();

  const budgeted = useMemo(() => {
    const today = moment.tz(user.timezone);

    let _budgeted = 0;

    trackingKpiData.dailyMetrics.forEach((i) => {
      if (moment(i.date).isSameOrBefore(today)) {
        _budgeted += i.planningTarget ?? 0;
      }
    });

    return _budgeted;
  }, [trackingKpiData, user.timezone]);
  return { budgetedKPIToDate: budgeted, loading };
}

export const KpiTile = ({ data }: Props) => {
  const {
    percentOfCycleCompleted,
    formattedMixToDateRange,
    formattedCycleDateRange,
    kpiFormatter,
  } = useClientSettings();
  const { budgetedKPIToDate, loading } = useBudgetedKPIToDate();

  if (!data || loading) {
    return (
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="border border-v2-secondary rounded w-full h-[270px] flex-center"
        >
          <img className="h-1/2" src={cardLoading} alt="Loading" />
        </motion.div>
      </AnimatePresence>
    );
  }

  const { predictedRange, actual } = data;
  const targetFormatted = kpiFormatter(predictedRange.median);
  const isCompleted = percentOfCycleCompleted === 1;

  return (
    <div className="border border-v2-secondary rounded p-4 w-full min-w-0">
      <div className="flex-1 flex justify-between mb-3 min-w-0">
        <div className="flex-grow truncate font-semibold text-xl mr-2">
          {data.name}
        </div>
        <PacingStatus
          className="rounded px-3 h-7 font-semibold text-xs"
          prefix={isCompleted ? "Completed" : "Pacing"}
          actual={actual}
          budgeted={budgetedKPIToDate}
          subject="forecast"
        />
      </div>
      <div className="text-xs tracking-wide relative flex items-center justify-between">
        <div className="bg-cosmic opacity-10 rounded-t absolute h-7 w-full"></div>
        <div className="py-1.5 pl-2 flex items-center space-x-2 font-semibold">
          <div className="tracking-wide">
            MIX {isCompleted ? "COMPLETED" : "TO DATE"}
          </div>
          <div className="text-cosmic text-xs">
            {`${formatting.formatPercentage(percentOfCycleCompleted, {
              precision: "approximate",
            })} complete`}
          </div>
        </div>
        <div className="py-1.5 pr-2 text-gray+2">{formattedMixToDateRange}</div>
      </div>
      <div className="h-36 bg-v2-secondary flex items-center">
        <Slider
          actualColor="#5CBF64"
          actual={actual}
          budgeted={budgetedKPIToDate}
          budgetedText="FORECASTED"
          target={predictedRange.median}
          formatter={kpiFormatter}
          dateRangeText={formattedMixToDateRange}
          targetIconTooltipContent={
            <div>
              <div className="text-xs font-semibold tracking-wide mb-1">
                TOTAL MIX TARGET RANGE
              </div>
              <div className="text-gray+2 font-normal">
                {formattedCycleDateRange}
              </div>
              <PredictedRange
                className="text-lg text-cosmic"
                predictedRange={predictedRange}
                formatter={kpiFormatter}
                targetFormatted={targetFormatted}
              />
            </div>
          }
        />
      </div>
      <div className="flex items-center justify-between gap-4 mt-2">
        <div className="flex flex-col text-xs">
          <div className="font-semibold tracking-wide flex items-center">
            TOTAL PERIOD FORECAST
            <Tooltip
              content={
                <div className="w-72">
                  KPI numbers are more likely to align with the prediction if
                  budgeted spend levels are followed.
                </div>
              }
            />
          </div>
          <div className="text-gray+2">{formattedCycleDateRange}</div>
        </div>
        <PredictedRange
          className="text-xl"
          predictedRange={predictedRange}
          formatter={kpiFormatter}
          targetFormatted={targetFormatted}
        />
      </div>
    </div>
  );
};

const PredictedRange = ({
  predictedRange,
  formatter,
  targetFormatted,
  className,
}: {
  predictedRange: {
    low: number;
    median: number;
    high: number;
  };
  formatter: (value: number, precision: MetricPrecision) => string;
  targetFormatted: string;
  className?: string;
}) => (
  <div className={clsx("min-w-[216px]", className)}>
    <div className="flex items-center  space-x-1 w-full">
      <i className="fa-regular fa-bullseye-arrow text-lg rounded-full p-1 text-interstellar-1" />
      <div className="text-xs self-center text-interstellar">
        {formatter(predictedRange.low, "approximate")}
      </div>
      <div
        className="flex-1 h-0.5 rounded-full"
        style={{
          background: "linear-gradient(90deg, #F05F33 0%, #FFD247 100%)",
        }}
      />
      <div className="font-semibold">{targetFormatted}</div>
      <div
        className="flex-1 h-0.5 rounded-full"
        style={{
          background: "linear-gradient(90deg, #FFD247 0%, #50BA58 100%)",
        }}
      />
      <div className="shrink-0 text-xs self-center text-interstellar">
        {formatter(predictedRange.high, "approximate")}
      </div>
    </div>
  </div>
);
